import Honeybadger from "@honeybadger-io/js"
import { createFragment } from "../lib/helpers"

const RETRY_DELAYS = [5_000, 25_000, 120_000, 300_000] // 5s, 25s, 2min, 5min
const originalFrameSrcs = new Map()
const retryStates = new Map()
const BANNER_ID = "turbo-frame-retry-banner"

// Capture original frame source URLs before Turbo fetches them.
// By the time turbo:frame-missing fires, the frame's src has already
// been updated to the redirect target (e.g., /sign_in), so we need
// to store the original URL here.
document.addEventListener("turbo:before-fetch-request", event => {
  if (event.target.tagName !== "TURBO-FRAME") return

  const frameId = event.target.id
  if (frameId && !originalFrameSrcs.has(frameId)) {
    originalFrameSrcs.set(frameId, event.detail.url.href)
  }
})

// Clean up retry state when a frame loads successfully
document.addEventListener("turbo:frame-load", event => {
  if (retryStates.delete(event.target.id) && retryStates.size === 0) {
    removeRetryBanner()
  }
})

// Clean up all state on page navigation
document.addEventListener("turbo:visit", () => {
  retryStates.forEach(state => clearTimeout(state.timer))
  retryStates.clear()
  originalFrameSrcs.clear()
  removeRetryBanner()
})

// Retry frame loads that fail due to redirects (e.g., session expiry)
document.addEventListener("turbo:frame-missing", event => {
  if (!event.detail.response.redirected) return

  event.preventDefault()

  const frame = event.target
  const originalSrc = originalFrameSrcs.get(frame.id)
  if (!originalSrc) return

  let state = retryStates.get(frame.id)
  if (!state) {
    state = { attempt: 0, originalSrc, timer: null }
    retryStates.set(frame.id, state)
  }

  if (state.attempt >= RETRY_DELAYS.length) {
    updateRetryBanner(true)
    notifyHoneybadger(frame, event.detail.response)
    return
  }

  const delay = RETRY_DELAYS[state.attempt]
  state.attempt++
  updateRetryBanner(false)

  state.timer = setTimeout(() => {
    frame.removeAttribute("complete")
    frame.src = state.originalSrc
  }, delay)
})

function updateRetryBanner(final) {
  let banner = document.getElementById(BANNER_ID)

  const message = final
    ? "We're having trouble loading some content. Please try refreshing the page or contact support if this continues."
    : "We're having trouble loading some content. Retrying shortly&hellip;"

  const alertType = final ? "danger" : "warning"

  if (!banner) {
    const flash = document.getElementById("flash")
    if (!flash) return

    const html = `<div id="${BANNER_ID}" class="alert alert-dismissible alert-${alertType} fade show" role="alert" data-controller="alert" data-alert-auto-dismiss-value="false">${message}<button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button></div>`
    flash.appendChild(createFragment(html))
  } else {
    banner.className = `alert alert-dismissible alert-${alertType} fade show`
    const closeBtn = '<button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>'
    banner.innerHTML = message + closeBtn
  }
}

function removeRetryBanner() {
  document.getElementById(BANNER_ID)?.remove()
}

function notifyHoneybadger(frame, response) {
  Honeybadger.notify({
    message: "Turbo frame missing after retries exhausted",
    name: "turbo:frame-missing",
    context: {
      targetId: frame.id,
      targetSource: frame.src,
      responseUrl: response.url,
      responseRedirected: response.redirected,
      responseStatus: response.status,
    }
  })
}
